import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
    CContainer, CRow, CCol, CCard, CCardHeader, CCardBody, CButton, CSpinner, 
    CListGroup, CListGroupItem, CFormCheck, CModal, CModalHeader, CModalTitle, 
    CModalBody, CModalFooter, CFormInput, CAccordion, CAccordionItem, 
    CAccordionHeader, CAccordionBody, CButtonGroup, CInputGroup, CBadge, 
    CAlert, CForm, CFormLabel, CFormSelect 
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
    cilArrowLeft, cilSave, cilCloudDownload, cilCast, cilSync, cilTrash, 
    cilActionUndo, cilPencil, cilCheck, cilX, cilCamera, cilWarning, 
    cilTask, cilCarAlt, cilChart, cilMove, cilCommentBubble, cilInfo, cilPlus,
    cilOptions
} from '@coreui/icons';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading/Loading';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from 'date-fns/locale/ru';
import enUS from 'date-fns/locale/en-US';
import dayjs from 'dayjs';
import InteractiveTimeline from '../../components/InteractiveTimeline/InteractiveTimeline';
import { ResponsivePie } from '@nivo/pie';
import Draggable from 'react-draggable';
import './CameraDetailPage.css';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';

registerLocale('ru', ru);
registerLocale('en', enUS);

// --- ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ---

const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '00:00:00';
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

// Функция для трансформации таймлайна (только для режима Auto Service)
const transformTimelineForType = (timeline, type) => {
    if (!timeline) return [];
    return timeline.map(segment => {
        let class_id = -1;
        let count = 0;

        if (type === 'chair') {
            count = segment.chair_people_count || 0;
            class_id = count > 0 ? -31 : -30;
        } else if (type === 'phone') {
            count = segment.phone_people_count || 0;
            class_id = count > 0 ? -41 : -40;
        }
        
        if (segment.class_id === -1 || segment.class_id === -20) {
            class_id = -1; 
            count = 0;
        }

        return {
            ...segment,
            class_id: class_id,
            people_count: count
        };
    });
};

// Генератор градиентной карты цветов
const generateGradientMetaMap = (baseId, idleId, colors, labelBase, idleLabel) => {
    const meta = {};
    // Класс простоя (0 людей)
    meta[String(idleId)] = { label: idleLabel, color: '#6c757d' }; // Всегда серый
    
    // Классы активности (от 1 до 10+)
    // colors[0] = 1 человек, colors[1] = 2 человека, ..., colors[10] = 10+
    for (let i = 1; i <= 10; i++) {
        const id = baseId - i; // -100 - 1 = -101
        const color = colors[i - 1] || colors[colors.length - 1];
        meta[String(id)] = { 
            label: `${labelBase} (${i} чел.)`, 
            color: color 
        };
    }
    // Класс 10+
    meta[String(baseId - 11)] = { 
        label: `${labelBase} (10+ чел.)`, 
        color: colors[10] || '#000000' 
    };
    
    meta['default'] = { label: 'Unknown', color: '#ccc' };
    return meta;
};

// --- ЦВЕТОВЫЕ ПАЛИТРЫ ---
// От светлого к темному
const GREEN_GRADIENT = ['#a5d6a7', '#81c784', '#66bb6a', '#4caf50', '#43a047', '#388e3c', '#2e7d32', '#1b5e20', '#0d3610', '#031f05', '#000000'];
const BLUE_GRADIENT = ['#90caf9', '#64b5f6', '#42a5f5', '#2196f3', '#1e88e5', '#1976d2', '#1565c0', '#0d47a1', '#08327d', '#041f52', '#000000'];
const ORANGE_GRADIENT = ['#ffcc80', '#ffb74d', '#ffa726', '#ff9800', '#fb8c00', '#f57c00', '#ef6c00', '#e65100', '#bf360c', '#8a2708', '#000000'];


// --- КОМПОНЕНТ РЕДАКТОРА ROI ---
const RoiEditor = ({ 
    snapshotUrl, 
    polygonPoints, 
    setPolygonPoints, 
    isLive, 
    showRoi, 
    onSave, 
    onReset, 
    onClear, 
    cameraName,
    onImageLoad
}) => {
    const { t } = useTranslation();
    const imgRef = useRef(null);
    const svgRef = useRef(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 }); 
    const [displayedSize, setDisplayedSize] = useState({ width: 0, height: 0 }); 
    const [draggingPoint, setDraggingPoint] = useState(null);

    const isModified = useMemo(() => {
        if (!polygonPoints || !polygonPoints.initial || !polygonPoints.current) return false;
        return JSON.stringify(polygonPoints.initial) !== JSON.stringify(polygonPoints.current);
    }, [polygonPoints]);

    const handleImageLoad = () => {
        if (imgRef.current) {
            const { naturalWidth, naturalHeight, clientWidth, clientHeight } = imgRef.current;
            setImageSize({ width: naturalWidth, height: naturalHeight });
            setDisplayedSize({ width: clientWidth, height: clientHeight });
            onImageLoad({ naturalWidth, naturalHeight });
        }
    };

    const handleCanvasClick = (e) => {
        if (!showRoi || isLive || !svgRef.current || imageSize.width === 0 || displayedSize.width === 0) return;
        
        const rect = svgRef.current.getBoundingClientRect();
        const scaleX = imageSize.width / displayedSize.width;
        const scaleY = imageSize.height / displayedSize.height;
        
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;
        
        setPolygonPoints({
            ...polygonPoints, 
            current: [...polygonPoints.current, { x, y }]
        });
    };

    const handlePointMouseDown = (e, index) => {
        e.stopPropagation();
        if (!showRoi || isLive) return;
        setDraggingPoint(index);
    };

    const handleSvgMouseMove = (e) => {
        if (draggingPoint === null || !svgRef.current) return;
        
        const rect = svgRef.current.getBoundingClientRect();
        const scaleX = imageSize.width / displayedSize.width;
        const scaleY = imageSize.height / displayedSize.height;
        
        const x = Math.max(0, Math.min(imageSize.width, (e.clientX - rect.left) * scaleX));
        const y = Math.max(0, Math.min(imageSize.height, (e.clientY - rect.top) * scaleY));
        
        const newPoints = [...polygonPoints.current];
        newPoints[draggingPoint] = { x, y };

        setPolygonPoints({
            ...polygonPoints, 
            current: newPoints
        });
    };

    const handleSvgMouseUp = () => setDraggingPoint(null);

    const handlePointRightClick = (e, indexToDelete) => {
        e.preventDefault();
        if (!showRoi || isLive) return;
        
        setPolygonPoints({
            ...polygonPoints, 
            current: polygonPoints.current.filter((_, index) => index !== indexToDelete)
        });
    };

    const pointsToRender = polygonPoints?.current || [];

    return (
        <CCard className="mb-4">
            <CCardHeader>{t('camera.title_roi')} <strong>{cameraName ? `- ${cameraName}` : ''}</strong></CCardHeader>
            <CCardBody>
                {snapshotUrl ? (
                    <>
                        <div style={{ position: 'relative', cursor: isLive || !showRoi ? 'default' : 'crosshair', display: 'inline-block', width: '100%' }}>
                            <img 
                                ref={imgRef} 
                                src={snapshotUrl} 
                                alt={`${cameraName} Snapshot`} 
                                style={{ width: '100%', height: 'auto', display: 'block' }} 
                                onLoad={handleImageLoad} 
                            />
                            <svg 
                                ref={svgRef} 
                                width="100%" 
                                height="100%" 
                                viewBox={`0 0 ${imageSize.width} ${imageSize.height}`} 
                                style={{ position: 'absolute', top: 0, left: 0, pointerEvents: imageSize.width > 0 ? 'auto' : 'none' }} 
                                onClick={handleCanvasClick}
                                onMouseMove={handleSvgMouseMove}
                                onMouseUp={handleSvgMouseUp}
                                onMouseLeave={handleSvgMouseUp}
                            >
                                {showRoi && pointsToRender.length > 0 && (
                                    <polygon 
                                        points={pointsToRender.map(p => `${p.x},${p.y}`).join(' ')} 
                                        style={{ 
                                            fill: 'rgba(255, 0, 0, 0.3)', 
                                            stroke: 'red', 
                                            strokeWidth: 4,
                                            vectorEffect: 'non-scaling-stroke', 
                                            pointerEvents: 'none' 
                                        }} 
                                    />
                                )}
                                {showRoi && pointsToRender.map((p, i) => (
                                    <circle 
                                        key={i} 
                                        cx={p.x} cy={p.y} 
                                        r="10"
                                        fill="white" stroke="red" strokeWidth="2" 
                                        onMouseDown={(e) => handlePointMouseDown(e, i)} 
                                        onContextMenu={(e) => handlePointRightClick(e, i)} 
                                        style={{ 
                                            cursor: isLive ? 'not-allowed' : 'move', 
                                            vectorEffect: 'non-scaling-stroke' 
                                        }}
                                    />
                                ))}
                            </svg>
                        </div>
                         {showRoi && (
                            <CButtonGroup className="mt-3 w-100">
                                <CButton color="primary" onClick={onSave} disabled={!isModified || isLive}>
                                    <CIcon icon={cilSave} className="me-2" /> {t('common.save')}
                                </CButton>
                                <CButton color="secondary" onClick={onReset} disabled={!isModified || isLive}>
                                    <CIcon icon={cilActionUndo} className="me-2" /> {t('common.reset')}
                                </CButton>
                                <CButton color="danger" variant="outline" onClick={onClear} disabled={isLive || pointsToRender.length === 0}>
                                    <CIcon icon={cilTrash} className="me-2" /> {t('common.clear')}
                                </CButton>
                            </CButtonGroup>
                        )}
                        <small className="form-text text-muted mt-2 d-block">
                            {t('camera.roi_hint')}
                        </small>
                    </>
                ) : <div className="text-center p-3"><CSpinner /></div>}
            </CCardBody>
        </CCard>
    );
};

// --- КОМПОНЕНТ БЛОКА АНАЛИТИКИ (График + Таймлайн) ---
const AnalyticsBlock = React.memo(({ 
    title, 
    analyticsData, 
    classIdToMeta, 
    onSegmentDoubleClick, 
    localTimezoneOffset, 
    timelineDataOverride,
    type = 'general',
    operatingMode
}) => {
    const { t } = useTranslation();

    const pieData = useMemo(() => {
        if (!analyticsData) return [];
        
        let data = [];
        let totalWorkSeconds = 0;

        if (analyticsData.durations_by_people_count) {
            totalWorkSeconds = analyticsData.durations_by_people_count.reduce((sum, item) => sum + item.total_duration_seconds, 0);
        }

        // Общий простой (0 людей)
        if (analyticsData.total_downtime_seconds > 0) {
            data.push({ 
                id: 'Downtime', 
                label: operatingMode === 'personnel_control' ? t('camera.downtime_simple') : t('camera.no_people_state'), 
                value: analyticsData.total_downtime_seconds, 
                color: '#6c757d' 
            });
        }
        
        // Ожидание (только для авто)
        if (analyticsData.total_idle_time_seconds > 0) {
            data.push({ 
                id: 'Idle', 
                label: t('classes.idle_vehicle'), 
                value: analyticsData.total_idle_time_seconds, 
                color: '#ffc107' 
            });
        }

        // Активность
        if (totalWorkSeconds > 0) {
            let label = t('camera.activity_legend_work'); // "Работа"
            let color = '#2eb85c'; // General Green Base

            if (type === 'chair') {
                label = t('camera.activity_legend_chair'); // "Сидят"
                color = '#3399ff'; // Blue Base
            } else if (type === 'phone') {
                label = t('camera.activity_legend_phone'); // "Телефон"
                color = '#ff9933'; // Orange Base
            }

            data.push({ 
                id: 'Working', 
                label: label, 
                value: totalWorkSeconds, 
                color: color
            });
        }
        
        return data;
    }, [analyticsData, t, type, operatingMode]);

    const rawTimeline = timelineDataOverride || analyticsData.timeline;
    
    const { localTimelineData, localDateRange } = useMemo(() => {
        if (!rawTimeline) return { localTimelineData: null, localDateRange: null };
        const offset = localTimezoneOffset || 0;
        
        const convertedTimeline = rawTimeline.map(segment => ({
            ...segment,
            start: dayjs(segment.start).add(offset, 'hour').toISOString(),
            end: dayjs(segment.end).add(offset, 'hour').toISOString(),
        }));
        
        let startStr, endStr;
        if (analyticsData.date_range) {
             startStr = analyticsData.date_range.start;
             endStr = analyticsData.date_range.end;
        } else {
             startStr = rawTimeline[0]?.start;
             endStr = rawTimeline[rawTimeline.length - 1]?.end;
        }

        const convertedDateRange = {
            start: dayjs(startStr).add(offset, 'hour').toISOString(),
            end: dayjs(endStr).add(offset, 'hour').toISOString(),
        };
        return { localTimelineData: convertedTimeline, localDateRange: convertedDateRange };
    }, [rawTimeline, analyticsData, localTimezoneOffset]);

    return (
        <CCard className="mb-4 shadow-sm border-top-primary border-top-3">
            <CCardHeader className="bg-white"><strong>{title}</strong></CCardHeader>
            <CCardBody>
                <CRow>
                    <CCol md={6}>
                        <CListGroup flush>
                            <CListGroupItem>
                                <strong>
                                    {operatingMode === 'personnel_control' 
                                        ? t('camera.downtime_simple') 
                                        : t('camera.total_downtime')}
                                :</strong> {formatDuration(analyticsData.total_downtime_seconds)}
                            </CListGroupItem>
                            
                            {analyticsData.total_idle_time_seconds > 0 && (
                                <CListGroupItem><strong>{t('camera.total_idle')}:</strong> {formatDuration(analyticsData.total_idle_time_seconds)}</CListGroupItem>
                            )}
                            
                            <CListGroupItem>
                                <strong>
                                    {type === 'chair' 
                                        ? t('camera.activity_chair_title') 
                                        : type === 'phone' 
                                            ? t('camera.activity_phone_title') 
                                            : t('camera.work_time')}:
                                </strong> {formatDuration(analyticsData.durations_by_people_count?.reduce((sum, item) => sum + item.total_duration_seconds, 0) || 0)}
                            </CListGroupItem>
                            
                            <CListGroupItem><strong>{t('camera.max_people')}:</strong> {analyticsData.max_concurrent_people}</CListGroupItem>
                            
                            {/* Детализация по людям с цветными точками */}
                            {analyticsData.durations_by_people_count?.length > 0 && (
                                <div className="mt-2 pt-2 border-top">
                                    <small className="text-muted d-block mb-1">{t('camera.detail_people_count')}</small>
                                    {analyticsData.durations_by_people_count.map(item => {
                                        // Вычисляем цвет для точки
                                        let metaId;
                                        if (type === 'general') metaId = -100 - item.people_count;
                                        else if (type === 'chair') metaId = -300 - item.people_count;
                                        else if (type === 'phone') metaId = -400 - item.people_count;
                                        
                                        // Если > 10
                                        if (item.people_count >= 10) metaId = metaId - 1; // Коррекция для 10+ если нужно, но в мапе мы это учли

                                        const meta = classIdToMeta[String(metaId)] || classIdToMeta['default'];

                                        return (
                                            <div key={item.people_count} className="d-flex justify-content-between small px-2 align-items-center">
                                                <span>
                                                    <span style={{display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: meta?.color || '#ccc', marginRight: '6px'}}></span>
                                                    {item.people_count} чел:
                                                </span>
                                                <span>{formatDuration(item.total_duration_seconds)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CListGroup>
                    </CCol>
                    <CCol md={6}>
                        {pieData.length > 0 ? (
                            <div style={{ height: '200px' }}>
                                <ResponsivePie
                                    data={pieData}
                                    margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                                    innerRadius={0.6}
                                    padAngle={1}
                                    cornerRadius={3}
                                    activeOuterRadiusOffset={8}
                                    colors={{ datum: 'data.color' }}
                                    enableArcLabels={true}
                                    arcLabel={d => `${Math.round((d.value / (pieData.reduce((acc, v) => acc + v.value, 0))) * 100)}%`}
                                    enableArcLinkLabels={false}
                                    tooltip={({ datum }) => (
                                        <div style={{ padding: '5px 10px', background: 'white', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }}>
                                            <div style={{ width: '10px', height: '10px', background: datum.color, display: 'inline-block', marginRight: '5px' }}></div>
                                            <strong>{datum.label}</strong>: {formatDuration(datum.value)}
                                        </div>
                                    )}
                                />
                            </div>
                        ) : <p className="text-muted text-center m-0 pt-5">{t('camera.no_videos')}</p>}
                    </CCol>
                </CRow>
                
                <h6 className="mt-4 mb-2 text-primary">{t('camera.timeline_local')}</h6>
                {localTimelineData ? (
                    <InteractiveTimeline 
                        timelineData={localTimelineData} 
                        dateRange={localDateRange} 
                        onItemDoubleClick={onSegmentDoubleClick} 
                        classIdToMeta={classIdToMeta} 
                    />
                ) : <p className="text-muted small">Нет данных для временной шкалы.</p>}
            </CCardBody>
        </CCard>
    );
});


const CameraDetailPage = () => {
    const { t, i18n } = useTranslation();
    const { cameraId } = useParams();
    
    // --- ОСНОВНЫЕ СОСТОЯНИЯ ---
    const [camera, setCamera] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // --- ИЗОБРАЖЕНИЯ И ROI ---
    const [snapshots, setSnapshots] = useState({ primary: null, secondary: null });
    const [imageSizes, setImageSizes] = useState({ 
        primary: { naturalWidth: 0, naturalHeight: 0 }, 
        secondary: { naturalWidth: 0, naturalHeight: 0 } 
    });
    const [polygons, setPolygons] = useState({
        primary: { initial: [], current: [] },
        secondary: { initial: [], current: [] }
    });
    
    // --- ВИДЕО ---
    const [videos, setVideos] = useState([]); 
    const [selectedVideos, setSelectedVideos] = useState({ primary: null, secondary: null });
    const [sourceCameraNames, setSourceCameraNames] = useState({ primary: '', secondary: '' });

    // --- АНАЛИТИКА ---
    const [analytics, setAnalytics] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    
    // --- УПРАВЛЕНИЕ ОТОБРАЖЕНИЕМ ---
    const [activeView, setActiveView] = useState('general'); // 'general', 'chair', 'phone'

    // --- РЕДАКТИРОВАНИЕ ИМЕНИ ---
    const [isEditingName, setIsEditingName] = useState(false);
    const [editingNameValue, setEditingNameValue] = useState('');
    
    // --- НАСТРОЙКИ ---
    const [settings, setSettings] = useState({
        work_start_time: '08:00', work_end_time: '18:00', timezone_offset: 0,
        vehicle_roi_threshold: 10, person_roi_threshold: 1, vehicle_log_merge_seconds: 120,
        vehicle_roi_occupancy_threshold: 1, person_roi_occupancy_threshold: 0.1,
        orb_similarity_threshold: 500,
        vehicle_confidence_threshold: 10,
        person_confidence_threshold: 10,
        workday_monday: true, workday_tuesday: true, workday_wednesday: true,
        workday_thursday: true, workday_friday: true, workday_saturday: false,
        workday_sunday: false,
        operating_mode: 'auto_service',
        count_squatting_as_sitting: false,
    });
    
    // --- ИСКЛЮЧЕНИЯ ---
    const [overrides, setOverrides] = useState([]);
    const [loadingOverrides, setLoadingOverrides] = useState(false);
    const [newOverride, setNewOverride] = useState({ date: new Date(), is_working: false, description: '' });
    
    // --- REFS ---
    const videoRefPrimary = useRef(null);
    const videoRefSecondary = useRef(null);
    
    // --- ДАТЫ ---
    const [startDate, setStartDate] = useState(dayjs().startOf('day').toDate());
    const [endDate, setEndDate] = useState(dayjs().endOf('day').toDate());
    
    // --- UI ФЛАГИ ---
    const [isLive, setIsLive] = useState(false);
    const [showRoi, setShowRoi] = useState(true);
    const [roiDebugMode, setRoiDebugMode] = useState(false);
    const [roiDebugInfo, setRoiDebugInfo] = useState(null);
    
    // --- МОДАЛКИ ---
    const [recalculateModalVisible, setRecalculateModalVisible] = useState(false);
    const [recalculationDate, setRecalculationDate] = useState(dayjs().startOf('day').toDate());
    const [isRecalculating, setIsRecalculating] = useState(false);
    
    const [llmModalVisible, setLlmModalVisible] = useState(false);
    const [llmSyncDate, setLlmSyncDate] = useState(dayjs().startOf('day').toDate());
    const [isLlmSyncing, setIsLlmSyncing] = useState(false);
    
    const [moveModal, setMoveModal] = useState({ visible: false, log: null, targetGroupId: '' });

    // --- МЕТАДАННЫЕ КЛАССОВ (ЦВЕТА И НАЗВАНИЯ) ---
    
    // 1. Auto Service (Vehicle Logic)
    const autoServiceClassIdToMeta = useMemo(() => ({
        '-1':   { label: t('classes.downtime'), color: '#6c757d' },
        '-2':   { label: t('classes.idle_vehicle'), color: '#ffc107' },
        '-101': { label: t('classes.vehicle_w_people', {count: 1}), color: '#d1e7dd' },
        '-102': { label: t('classes.vehicle_w_people', {count: 2}), color: '#afdac0' },
        '-103': { label: t('classes.vehicle_w_people', {count: 3}), color: '#8dcd9f' },
        '-104': { label: t('classes.vehicle_w_people', {count: 4}), color: '#6bbf7e' },
        '-105': { label: t('classes.vehicle_w_people', {count: 5}), color: '#4ab25e' },
        '-106': { label: t('classes.vehicle_w_people', {count: 6}), color: '#389e4c' },
        '-107': { label: t('classes.vehicle_w_people', {count: 7}), color: '#2a8b3d' },
        '-108': { label: t('classes.vehicle_w_people', {count: 8}), color: '#1d782e' },
        '-109': { label: t('classes.vehicle_w_people', {count: 9}), color: '#106520' },
        '-110': { label: t('classes.vehicle_w_people', {count: 10}), color: '#035211' },
        '-111': { label: t('classes.vehicle_w_people', {count: '10+'}), color: '#00400c' },
        'default': { label: t('classes.unknown'), color: '#ccc'}
    }), [t]);

    // 2. Personnel Control (General) - Градиент Зеленый
    const personnelGeneralMeta = useMemo(() => {
        return generateGradientMetaMap(-100, -1, GREEN_GRADIENT, t('camera.presence_state'), t('classes.downtime_no_people'));
    }, [t]);

    // 3. Chair - Градиент Синий
    const chairMeta = useMemo(() => {
        const meta = generateGradientMetaMap(-300, -30, BLUE_GRADIENT, t('camera.chair_state'), t('camera.no_people_state'));
        
        // Добавляем класс для совместимости с режимом "Автосервис" (просто факт наличия)
        meta['-31'] = { label: t('camera.chair_state'), color: '#3399ff' }; 
        
        return meta;
    }, [t])

    // 4. Phone - Градиент Оранжевый
    const phoneMeta = useMemo(() => {
        const meta = generateGradientMetaMap(-400, -40, ORANGE_GRADIENT, t('camera.phone_state'), t('camera.no_people_state'));
        
        // Добавляем класс для совместимости с режимом "Автосервис" (просто факт наличия)
        meta['-41'] = { label: t('camera.phone_state'), color: '#ff9933' }; 
        
        return meta;
    }, [t]);


    // --- ВЫЧИСЛЯЕМЫЕ ДАННЫЕ (MEMOIZED) ---

    // Группировка логов (ТОЛЬКО ДЛЯ AUTO SERVICE)
    const groupedVehicleLogs = useMemo(() => {
        if (camera?.operating_mode !== 'auto_service' || !analytics?.vehicle_logs || analytics.vehicle_logs.length === 0) return [];
        const logsById = new Map(analytics.vehicle_logs.map(log => [log.id, log]));
        const commentsMap = new Map(analytics.vehicle_analysis_comments.map(c => [c.first_vehicle_log_id, c]));
        const sortedLogIds = analytics.vehicle_logs.map(l => l.id).sort((a, b) => dayjs(logsById.get(a).entry_time).diff(dayjs(logsById.get(b).entry_time)));
        
        const groups = new Map();
        let currentGroupId = 1;

        for (let i = 0; i < sortedLogIds.length; i++) {
            const logId = sortedLogIds[i];
            if (i === 0) {
                groups.set(currentGroupId, [logId]);
                continue;
            }
            const prevLogId = sortedLogIds[i-1];
            const comparisonComment = commentsMap.get(prevLogId);
            if (comparisonComment && comparisonComment.is_same_vehicle) {
                groups.get(currentGroupId).push(logId);
            } else {
                currentGroupId++;
                groups.set(currentGroupId, [logId]);
            }
        }
        
        return Array.from(groups.entries()).map(([groupId, logIds]) => {
            const logs = logIds.map(id => logsById.get(id));
            const totalManHours = logs.reduce((sum, log) => sum + (log.total_man_hours || 0), 0);
            return {
                id: groupId,
                logs: logs,
                startTime: logs[0].entry_time,
                endTime: logs[logs.length - 1].exit_time,
                total_man_hours: totalManHours,
            };
        }).sort((a,b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
    }, [analytics, camera]);

    // Данные для графиков
    const generalData = useMemo(() => {
        if (!analytics) return null;
        const stats = analytics.mode === 'auto_service' ? analytics.general_stats : analytics.general;
        return { 
            ...stats, 
            start_date: analytics.date_range.start, 
            end_date: analytics.date_range.end 
        };
    }, [analytics]);

    const chairData = useMemo(() => {
        if (!analytics) return null;
        const stats = analytics.mode === 'auto_service' ? analytics.chair_stats : analytics.chair;
        return { 
            ...stats, 
            start_date: analytics.date_range.start, 
            end_date: analytics.date_range.end 
        };
    }, [analytics]);

    const phoneData = useMemo(() => {
        if (!analytics) return null;
        const stats = analytics.mode === 'auto_service' ? analytics.phone_stats : analytics.phone;
        return { 
            ...stats, 
            start_date: analytics.date_range.start, 
            end_date: analytics.date_range.end 
        };
    }, [analytics]);

    // Таймлайны
    const chairTimelineToShow = useMemo(() => {
        if (!analytics) return [];
        if (analytics.mode === 'personnel_control') return analytics.chair.timeline;
        const sourceTimeline = analytics.general_stats?.timeline || analytics.timeline;
        return transformTimelineForType(sourceTimeline, 'chair');
    }, [analytics]);

    const phoneTimelineToShow = useMemo(() => {
        if (!analytics) return [];
        if (analytics.mode === 'personnel_control') return analytics.phone.timeline;
        const sourceTimeline = analytics.general_stats?.timeline || analytics.timeline;
        return transformTimelineForType(sourceTimeline, 'phone');
    }, [analytics]);


    // --- API ВЫЗОВЫ ---
    const fetchNamesForSourceCameras = async (cam) => {
        try {
            const token = localStorage.getItem('token');
            const primaryId = cam.parent || cam.id;
            const secondaryId = cam.secondary_camera;
            const names = { primary: 'Camera 1', secondary: 'Camera 2' };
            if (primaryId) {
                const res = await axios.get(`/api/cameras/${primaryId}/`, { headers: { Authorization: `Bearer ${token}` } });
                names.primary = res.data.name;
            }
            if (secondaryId) {
                const res = await axios.get(`/api/cameras/${secondaryId}/`, { headers: { Authorization: `Bearer ${token}` } });
                names.secondary = res.data.name;
            }
            setSourceCameraNames(names);
        } catch (e) { console.error("Failed to fetch camera names", e); }
    };

    const fetchSnapshots = async (cam) => {
        try {
            const token = localStorage.getItem('token');
            const primaryId = cam.parent || cam.id;
            const secondaryId = cam.secondary_camera;
            const urls = { primary: null, secondary: null };
            try {
                const primarySnapRes = await axios.get(`/api/cameras/${primaryId}/snapshot/`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                urls.primary = URL.createObjectURL(primarySnapRes.data);
            } catch (e) {}
            if (cam.is_combined && secondaryId) {
                try {
                    const secondarySnapRes = await axios.get(`/api/cameras/${secondaryId}/snapshot/`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                    urls.secondary = URL.createObjectURL(secondarySnapRes.data);
                } catch (e) {}
            }
            setSnapshots(prev => {
                if (prev.primary) URL.revokeObjectURL(prev.primary);
                if (prev.secondary) URL.revokeObjectURL(prev.secondary);
                return urls;
            });
        } catch (err) {
            console.error("Snapshot error");
            if (isLive) { setIsLive(false); toast.warn(t('common.error')); }
        }
    };

    const fetchOverrides = async () => {
        setLoadingOverrides(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/date-overrides/?camera_id=${cameraId}`, { headers: { Authorization: `Bearer ${token}` } });
            setOverrides(res.data.results);
        } catch (err) { toast.error(t('common.error')); } 
        finally { setLoadingOverrides(false); }
    };

    useEffect(() => {
        const fetchCameraData = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const camRes = await axios.get(`/api/cameras/${cameraId}/`, { headers: { Authorization: `Bearer ${token}` } });
                const cameraData = camRes.data;
                setCamera(cameraData);
                await fetchNamesForSourceCameras(cameraData);
                setSettings({
                    work_start_time: cameraData.work_start_time?.substring(0, 5) || '08:00',
                    work_end_time: cameraData.work_end_time?.substring(0, 5) || '18:00',
                    timezone_offset: cameraData.timezone_offset || 0,
                    vehicle_roi_threshold: Math.round(cameraData.vehicle_roi_threshold * 100),
                    person_roi_threshold: Math.round(cameraData.person_roi_threshold * 100),
                    vehicle_log_merge_seconds: cameraData.vehicle_log_merge_seconds,
                    vehicle_roi_occupancy_threshold: (cameraData.vehicle_roi_occupancy_threshold * 100).toFixed(2),
                    person_roi_occupancy_threshold: (cameraData.person_roi_occupancy_threshold * 100).toFixed(3),
                    orb_similarity_threshold: cameraData.orb_similarity_threshold || 500,
                    vehicle_confidence_threshold: (cameraData.vehicle_confidence_threshold * 100).toFixed(1),
                    person_confidence_threshold: (cameraData.person_confidence_threshold * 100).toFixed(1),
                    workday_monday: cameraData.workday_monday,
                    workday_tuesday: cameraData.workday_tuesday,
                    workday_wednesday: cameraData.workday_wednesday,
                    workday_thursday: cameraData.workday_thursday,
                    workday_friday: cameraData.workday_friday,
                    workday_saturday: cameraData.workday_saturday,
                    workday_sunday: cameraData.workday_sunday,
                    operating_mode: cameraData.operating_mode || 'auto_service',
                    count_squatting_as_sitting: cameraData.count_squatting_as_sitting,
                });
                const roi = cameraData.roi_polygon || {};
                if (cameraData.is_combined) {
                    setPolygons({ primary: { initial: roi.primary || [], current: [] }, secondary: { initial: roi.secondary || [], current: [] } });
                } else {
                    const points = Array.isArray(roi) ? roi : [];
                    setPolygons(prev => ({ ...prev, primary: { initial: points, current: [] } }));
                }
                await fetchSnapshots(cameraData);
                await fetchOverrides();
            } catch (err) { toast.error(t('common.error')); } 
            finally { setLoading(false); }
        };
        fetchCameraData();
    }, [cameraId, t]);

    useEffect(() => {
        let intervalId;
        if (isLive && camera) intervalId = setInterval(() => fetchSnapshots(camera), 1000);
        return () => clearInterval(intervalId);
    }, [isLive, camera]);

    useEffect(() => {
        let intervalId;
        const fetchRoiDebugInfo = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`/api/cameras/${cameraId}/roi-debug-info/`, { headers: { Authorization: `Bearer ${token}` } });
                setRoiDebugInfo(res.data);
            } catch (err) { setRoiDebugMode(false); }
        };
        if (roiDebugMode) { fetchRoiDebugInfo(); intervalId = setInterval(fetchRoiDebugInfo, 2000); } 
        else { setRoiDebugInfo(null); }
        return () => clearInterval(intervalId);
    }, [roiDebugMode, cameraId]);

    // --- HANDLERS ---
    const handleImageLoaded = (type, { naturalWidth, naturalHeight }) => {
        setImageSizes(prev => ({ ...prev, [type]: { naturalWidth, naturalHeight } }));
        setPolygons(prev => {
            const initialRelative = prev[type].initial;
            const absolutePoints = initialRelative.map(p => ({ x: p.x * naturalWidth, y: p.y * naturalHeight }));
            if (prev[type].current.length === 0 && initialRelative.length > 0) {
                return { ...prev, [type]: { ...prev[type], current: absolutePoints } };
            }
            return prev;
        });
    };

    const handleSaveRoi = async () => {
        if (!camera) return;
        let payload;
        const toRelative = (points, size) => points.map(p => ({ x: p.x / size.naturalWidth, y: p.y / size.naturalHeight }));
        if (camera.is_combined) {
            payload = { primary: toRelative(polygons.primary.current, imageSizes.primary), secondary: toRelative(polygons.secondary.current, imageSizes.secondary) };
        } else {
            payload = toRelative(polygons.primary.current, imageSizes.primary);
        }
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/cameras/${cameraId}/`, { roi_polygon: payload }, { headers: { Authorization: `Bearer ${token}` } });
            setPolygons(prev => ({
                primary: { ...prev.primary, initial: toRelative(prev.primary.current, imageSizes.primary) },
                secondary: { ...prev.secondary, initial: toRelative(prev.secondary.current, imageSizes.secondary) }
            }));
            toast.success(t('camera.roi_saved'));
        } catch (error) { toast.error(t('common.error')); }
    };

    const handleResetRoi = (type) => {
        const size = imageSizes[type];
        const initialRelative = polygons[type].initial;
        const absolute = initialRelative.map(p => ({ x: p.x * size.naturalWidth, y: p.y * size.naturalHeight }));
        setPolygons(prev => ({ ...prev, [type]: { ...prev[type], current: absolute } }));
    };

    const handleSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSaveSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            
            const payload = {
                ...settings,
                
                // Настройки людей
                person_roi_threshold: parseFloat(settings.person_roi_threshold) / 100,
                person_roi_occupancy_threshold: parseFloat(settings.person_roi_occupancy_threshold) / 100,
                person_confidence_threshold: parseFloat(settings.person_confidence_threshold) / 100,

                // Настройки техники (обязательно нормализуем, даже если скрыты!)
                vehicle_roi_threshold: parseFloat(settings.vehicle_roi_threshold) / 100,
                vehicle_roi_occupancy_threshold: parseFloat(settings.vehicle_roi_occupancy_threshold) / 100,
                vehicle_confidence_threshold: parseFloat(settings.vehicle_confidence_threshold) / 100,
                
                // Целочисленные настройки
                vehicle_log_merge_seconds: parseInt(settings.vehicle_log_merge_seconds, 10),
                orb_similarity_threshold: parseInt(settings.orb_similarity_threshold, 10),
            };

            const response = await axios.patch(`/api/cameras/${cameraId}/`, payload, { headers: { Authorization: `Bearer ${token}` } });
            setCamera(response.data);
            toast.success(t('camera.settings_saved_success'));
        } catch (error) { 
            console.error(error);
            if (error.response && error.response.data) {
                 if (typeof error.response.data === 'object') {
                     Object.entries(error.response.data).forEach(([key, msg]) => {
                         toast.error(`${key}: ${msg}`);
                     });
                 } else {
                     toast.error(t('common.error'));
                 }
            } else {
                toast.error(t('common.error')); 
            }
        }
    };

    const handleSegmentDoubleClick = useCallback(async (itemId) => {
        if (!analytics || !camera) return;
        
        let targetTime = null;
        let rawTimeline = [];

        if (analytics.mode === 'personnel_control') {
            if (activeView === 'general') rawTimeline = analytics.general.timeline;
            else if (activeView === 'chair') rawTimeline = analytics.chair.timeline;
            else if (activeView === 'phone') rawTimeline = analytics.phone.timeline;
        } else {
            rawTimeline = analytics.general_stats?.timeline || analytics.timeline;
        }
        
        if (rawTimeline && rawTimeline[itemId]) {
            targetTime = rawTimeline[itemId].start;
        }

        if (!targetTime) return;

        const offset = camera.timezone_offset || 0;
        
        try {
            const token = localStorage.getItem('token');
            const url = `/api/cameras/${cameraId}/get-frame-info/?timestamp=${targetTime}`;
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = response.data;
            const primaryId = camera.parent || camera.id;
            const secondaryId = camera.secondary_camera;
            const VIDEO_FPS = 24;
            const commonSeekId = Date.now();
            const newSelected = { primary: null, secondary: null };
            const primaryData = data[primaryId] || data[String(primaryId)];
            const secondaryData = camera.is_combined ? (data[secondaryId] || data[String(secondaryId)]) : null;

            if (primaryData) {
                newSelected.primary = { url: primaryData.video_file, seekTime: (primaryData.frame_id_in_folder - 1) / VIDEO_FPS, seekId: commonSeekId };
            }
            if (secondaryData) {
                newSelected.secondary = { url: secondaryData.video_file, seekTime: (secondaryData.frame_id_in_folder - 1) / VIDEO_FPS, seekId: commonSeekId };
            }
            if (!newSelected.primary && !newSelected.secondary) { toast.warn(t('camera.no_videos')); return; }
            setSelectedVideos(newSelected);
            const isPrimary = !!newSelected.primary;
            toast.info(t('camera.vehicle_video_loaded', {name: isPrimary ? sourceCameraNames.primary : sourceCameraNames.secondary}));
        } catch (error) { toast.error(error.response?.data?.error || t('common.error')); }
    }, [analytics, camera, cameraId, t, activeView, sourceCameraNames]);

    useEffect(() => {
        const loadAndSeekVideo = (videoRef, videoData, isPrimary) => {
            if (videoRef.current && videoData && videoData.seekId) {
                const vid = videoRef.current;
                const setPositionAndPause = () => {
                    vid.pause();
                    if (Number.isFinite(videoData.seekTime)) vid.currentTime = videoData.seekTime;
                };
                if (vid.readyState >= 1) setPositionAndPause();
                else vid.onloadedmetadata = () => setPositionAndPause();
                if (isPrimary) vid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        };
        if (selectedVideos.primary) loadAndSeekVideo(videoRefPrimary, selectedVideos.primary, true);
        if (camera?.is_combined && selectedVideos.secondary) loadAndSeekVideo(videoRefSecondary, selectedVideos.secondary, false);
    }, [selectedVideos, camera]);

    const fetchReports = async (start = startDate, end = endDate) => {
        if (dayjs(end).isBefore(dayjs(start))) { toast.warn(t('common.error')); return; }
        setLoadingAnalytics(true); setSelectedVideos({ primary: null, secondary: null }); setAnalytics(null); setVideos([]);
        try {
            const token = localStorage.getItem('token');
            const analyticsParams = new URLSearchParams({ camera_id: cameraId, start_datetime: start.toISOString(), end_datetime: end.toISOString() }).toString();
            const datesInRange = [];
            let currentDate = dayjs(start).startOf('day');
            const lastDate = dayjs(end).startOf('day');
            while (currentDate.isBefore(lastDate) || currentDate.isSame(lastDate)) {
                datesInRange.push(currentDate.format('YYYY-MM-DD'));
                currentDate = currentDate.add(1, 'day');
            }
            const analyticsPromise = axios.get(`/api/analytics/?${analyticsParams}`, { headers: { Authorization: `Bearer ${token}` } });
            const videoPromises = datesInRange.map(dateStr => axios.get(`/api/cameras/${cameraId}/videos/?date=${dateStr}`, { headers: { Authorization: `Bearer ${token}` } }));
            const [analyticsRes, ...videoResponses] = await Promise.all([analyticsPromise, ...videoPromises]);
            setAnalytics(analyticsRes.data);
            const processedVideos = [];
            videoResponses.forEach((res, idx) => {
                const date = datesInRange[idx];
                const dayData = res.data;
                if (Object.values(dayData).some(arr => arr && arr.length > 0)) processedVideos.push({ date: date, data: dayData });
            });
            setVideos(processedVideos.sort((a,b) => dayjs(b.date).diff(dayjs(a.date))));
        } catch (error) { toast.error(error.response?.data?.error || t('common.error')); } 
        finally { setLoadingAnalytics(false); }
    };

    const handleAddOverride = async () => {
        if (!newOverride.date) { toast.warn(t('common.fill_required')); return; }
        const dateString = dayjs(newOverride.date).format('YYYY-MM-DD');
        if (overrides.some(ov => ov.date === dateString)) { toast.error(t('common.error')); return; }
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/date-overrides/', { camera: cameraId, date: dateString, is_working: newOverride.is_working, description: newOverride.description }, { headers: { Authorization: `Bearer ${token}` } });
            setOverrides(prev => [...prev, {id: Date.now(), date: dateString, is_working: newOverride.is_working, description: newOverride.description}].sort((a, b) => dayjs(b.date).diff(dayjs(a.date))));
            await fetchOverrides();
            setNewOverride({ date: new Date(), is_working: false, description: '' });
            toast.success(t('camera.exception_added'));
        } catch (err) { toast.error(t('common.error')); }
    };

    const handleDeleteOverride = async (overrideId) => {
        if (!window.confirm(t('dashboard.confirm_delete'))) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/date-overrides/${overrideId}/`, { headers: { Authorization: `Bearer ${token}` } });
            setOverrides(prev => prev.filter(ov => ov.id !== overrideId));
            toast.success(t('camera.exception_deleted'));
        } catch (err) { toast.error(t('common.error')); }
    };

    const handleSaveName = async () => {
        if (!editingNameValue.trim()) { toast.warn(t('common.fill_required')); return; }
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/cameras/${cameraId}/`, { name: editingNameValue }, { headers: { Authorization: `Bearer ${token}` } });
            setCamera(prev => ({ ...prev, name: editingNameValue })); setIsEditingName(false); toast.success(t('camera.edit_name_success'));
        } catch (error) { toast.error(t('common.error')); }
    };

    const handleMoveLog = async () => {
        const { log, targetGroupId } = moveModal;
        if (!log || targetGroupId === '') { toast.warn(t('common.fill_required')); return; }
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/cameras/${cameraId}/move-vehicle-log/`, { log_id: log.id, target_log_id: targetGroupId }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(t('common.success')); setMoveModal({ visible: false, log: null, targetGroupId: '' }); fetchReports();
        } catch (error) { toast.error(error.response?.data?.error || t('common.error')); }
    };

    const handleRecalculate = async () => {
        setIsRecalculating(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`/api/cameras/${cameraId}/recalculate-analytics/`, { start_date: recalculationDate.toISOString() }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(response.data.message); setRecalculateModalVisible(false);
        } catch (error) { toast.error(t('common.error')); } finally { setIsRecalculating(false); }
    };

    const handleLlmSync = async () => {
        setIsLlmSyncing(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`/api/cameras/${cameraId}/recalculate-llm-analytics/`, { start_date: llmSyncDate.toISOString() }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(response.data.message); setLlmModalVisible(false);
        } catch (error) { toast.error(t('common.error')); } finally { setIsLlmSyncing(false); }
    };


    if (loading) return <Loading name={t('common.loading')} />;
    const formatLocalTime = (utcTime) => dayjs(utcTime).add(camera.timezone_offset, 'hour').format('DD.MM HH:mm');

    return (
        <div className="bg-light min-vh-100 p-4" style={{ position: 'relative' }}>
            <CContainer fluid>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    {isEditingName ? (
                        <CInputGroup style={{ maxWidth: '400px' }}>
                            <CFormInput value={editingNameValue} onChange={(e) => setEditingNameValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSaveName()} />
                            <CButton type="button" color="success" onClick={handleSaveName} className="text-white"><CIcon icon={cilCheck} /></CButton>
                            <CButton type="button" color="secondary" onClick={() => setIsEditingName(false)}><CIcon icon={cilX} /></CButton>
                        </CInputGroup>
                    ) : (
                        <div className="d-flex align-items-center">
                            <h2 className="mb-0 me-3">{camera?.name}</h2>
                            <CButton color="light" size="sm" onClick={() => { setEditingNameValue(camera.name); setIsEditingName(true); }} title={t('common.edit')}><CIcon icon={cilPencil} /></CButton>
                        </div>
                    )}
                    <div style={{display: "flex"}}>
                        <CButton color="secondary" as={Link} to="/dashboard"><CIcon icon={cilArrowLeft} className="me-2" /> {t('common.back')}</CButton>
                        <LanguageSwitcher />
                    </div>
                </div>

                {camera.is_duplicate && !camera.is_combined && <CAlert color="info" className="mb-4"><CIcon icon={cilInfo} className="me-2" />{t('camera.child_post_alert', {parentId: camera.parent})}</CAlert>}
                {camera.is_combined && <CAlert color="success" className="mb-4"><CIcon icon={cilInfo} className="me-2" /><strong>Совмещенный пост:</strong> {sourceCameraNames.primary} + {sourceCameraNames.secondary}</CAlert>}

                <CRow>
                    {/* LEFT COLUMN: ROI & SETTINGS */}
                    <CCol xl={5} className="mb-4">
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                            <div className='d-flex gap-3'>
                               <CFormCheck id="liveViewCheck" label={t('camera.live_view')} checked={isLive} onChange={() => setIsLive(!isLive)} />
                               <CFormCheck id="showRoiCheck" label={t('camera.show_roi')} checked={showRoi} onChange={() => setShowRoi(!showRoi)} />
                               <CFormCheck id="debugRoiCheck" label={t('camera.debug_roi')} checked={roiDebugMode} onChange={() => setRoiDebugMode(!roiDebugMode)} />
                            </div>
                            <CButton color='light' size='sm' onClick={() => fetchSnapshots(camera)} title={t('camera.update_snapshot')}><CIcon icon={cilCamera}/></CButton>
                        </div>
                        
                        <RoiEditor snapshotUrl={snapshots.primary} polygonPoints={polygons.primary} setPolygonPoints={newPoints => setPolygons(p => ({...p, primary: newPoints}))} isLive={isLive} showRoi={showRoi} onSave={handleSaveRoi} onReset={() => handleResetRoi('primary')} onClear={() => setPolygons(p => ({...p, primary: {...p.primary, current: []}}))} cameraName={sourceCameraNames.primary} onImageLoad={(dim) => handleImageLoaded('primary', dim)} />
                        {camera.is_combined && <RoiEditor snapshotUrl={snapshots.secondary} polygonPoints={polygons.secondary} setPolygonPoints={newPoints => setPolygons(p => ({...p, secondary: newPoints}))} isLive={isLive} showRoi={showRoi} onSave={handleSaveRoi} onReset={() => handleResetRoi('secondary')} onClear={() => setPolygons(p => ({...p, secondary: {...p.secondary, current: []}}))} cameraName={sourceCameraNames.secondary} onImageLoad={(dim) => handleImageLoaded('secondary', dim)} />}
                        
                        <CAccordion className="mb-4">
                            <CAccordionItem itemKey={1}>
                                <CAccordionHeader className="clickable-header">{t('camera.settings_analytics')}</CAccordionHeader>
                                <CAccordionBody>
                                    <CRow>
                                        <CCol xs={6}><CFormInput type="time" label={t('camera.shift_start')} name="work_start_time" value={settings.work_start_time} onChange={handleSettingsChange} /></CCol>
                                        <CCol xs={6}><CFormInput type="time" label={t('camera.shift_end')} name="work_end_time" value={settings.work_end_time} onChange={handleSettingsChange} /></CCol>
                                        <CCol xs={12} className="mt-3"><CFormLabel>{t('camera.workdays')}</CFormLabel><div className="d-flex flex-wrap gap-3">{['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => (<CFormCheck key={day} inline id={`wd-${day}`} label={day.charAt(0).toUpperCase() + day.slice(1,3)} name={`workday_${day}`} checked={settings[`workday_${day}`]} onChange={handleSettingsChange} />))}</div></CCol>
                                        <CCol xs={12} className="mt-4"><CFormLabel>{t('camera.exceptions')}</CFormLabel>{loadingOverrides ? <CSpinner size="sm"/> : (<><CListGroup style={{maxHeight: '200px', overflowY: 'auto'}} className="mb-3">{overrides.length > 0 ? overrides.map(ov => (<CListGroupItem key={ov.id} className="d-flex justify-content-between align-items-center"><div><strong>{dayjs(ov.date).format("DD.MM.YYYY")}</strong> - <CBadge color={ov.is_working ? 'success' : 'secondary'}>{ov.is_working ? t('camera.is_working_true') : t('camera.is_working_false')}</CBadge><div className="small text-muted">{ov.description}</div></div><CButton color="light" size="sm" onClick={() => handleDeleteOverride(ov.id)}><CIcon icon={cilTrash}/></CButton></CListGroupItem>)) : <CListGroupItem className="text-center text-muted">No exceptions</CListGroupItem>}</CListGroup><CRow className="g-2 align-items-end"><CCol xs={4}><CFormLabel className="small">{t('common.date_start')}</CFormLabel><DatePicker selected={newOverride.date} onChange={date => setNewOverride(p => ({...p, date}))} className="form-control" dateFormat="dd.MM.yyyy" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></CCol><CCol xs={5}><CFormInput label={<span className="small">{t('common.description')}</span>} value={newOverride.description} onChange={e => setNewOverride(p => ({...p, description: e.target.value}))} /></CCol><CCol xs={3} className="d-flex flex-column align-items-start"><CFormCheck label={t('camera.is_working')} checked={newOverride.is_working} onChange={e => setNewOverride(p => ({...p, is_working: e.target.checked}))} /><CButton color="primary" size="sm" className="mt-1" onClick={handleAddOverride}><CIcon icon={cilPlus}/> {t('common.add')}</CButton></CCol></CRow></>)}</CCol>
                                        <CCol xs={12} className="mt-3"><CFormInput type="number" label={t('camera.timezone_offset')} name="timezone_offset" value={settings.timezone_offset} onChange={handleSettingsChange} helpText={t('camera.timezone_help')}/></CCol>
                                        <hr className="my-4"/>
                                        
                                        {/* Auto Service Specific Settings */}
                                        {settings.operating_mode === 'auto_service' && (
                                            <>
                                                <CCol xs={6}><CFormInput type="number" label={t('camera.thresh_vehicle_roi')} name="vehicle_roi_threshold" value={settings.vehicle_roi_threshold} onChange={handleSettingsChange} min="0" max="100" /></CCol>
                                                <CCol xs={6}><CFormInput type="number" step="0.1" label={t('camera.thresh_vehicle_occ')} name="vehicle_roi_occupancy_threshold" value={settings.vehicle_roi_occupancy_threshold} onChange={handleSettingsChange} min="0" max="100" /></CCol>
                                                <CCol xs={12} className="mt-3"><CFormInput type="number" label={t('camera.merge_time')} name="vehicle_log_merge_seconds" value={settings.vehicle_log_merge_seconds} onChange={handleSettingsChange} min="30" max="3600" /></CCol>
                                                <CCol xs={12} className="mt-3"><CFormInput type="number" label={t('camera.orb_thresh')} name="orb_similarity_threshold" value={settings.orb_similarity_threshold} onChange={handleSettingsChange} min="10" max="2000" /></CCol>
                                                <CCol xs={6} className="mt-3 mb-3"><CFormInput type="number" label={t('camera.thresh_vehicle_conf')} name="vehicle_confidence_threshold" value={settings.vehicle_confidence_threshold} onChange={handleSettingsChange} min="0.5" max="90" step="0.1" /></CCol>
                                                <hr className="my-4"/>
                                            </>
                                        )}
                                        
                                        {/* Common / Person Settings */}
                                        <CCol xs={6}><CFormInput type="number" label={t('camera.thresh_person_roi')} name="person_roi_threshold" value={settings.person_roi_threshold} onChange={handleSettingsChange} min="0" max="100" /></CCol>
                                        <CCol xs={6}><CFormInput type="number" step="0.01" label={t('camera.thresh_person_occ')} name="person_roi_occupancy_threshold" value={settings.person_roi_occupancy_threshold} onChange={handleSettingsChange} min="0" max="100" /></CCol>
                                        <CCol xs={6} className="mt-3 mb-3"><CFormInput type="number" label={t('camera.thresh_person_conf')} name="person_confidence_threshold" value={settings.person_confidence_threshold} onChange={handleSettingsChange} min="0.5" max="90" step="0.1" /></CCol>
                                        
                                        {/* НОВОЕ ПОЛЕ */}
                                        <CCol xs={12} className="mt-2">
                                            <CFormCheck 
                                                id="countSquattingCheck" 
                                                label={t('camera.count_squatting')} 
                                                name="count_squatting_as_sitting" 
                                                checked={settings.count_squatting_as_sitting} 
                                                onChange={handleSettingsChange} 
                                            />
                                        </CCol>
                                    </CRow>
                                    <CButton color="primary" onClick={handleSaveSettings} className="mt-3 w-100"><CIcon icon={cilSave} className="me-2" /> {t('camera.save_settings')}</CButton>
                                </CAccordionBody>
                            </CAccordionItem>
                        </CAccordion>
                    </CCol>

                    {/* RIGHT COLUMN: ANALYTICS */}
                    <CCol xl={7}>
                        <CCard className="mb-4">
                            <CCardHeader className="d-flex justify-content-between align-items-center">
                                {t('camera.analytics_archive')}
                                <CButtonGroup>
                                    {camera?.operating_mode === 'auto_service' && <CButton color="info" variant="outline" size="sm" onClick={() => setLlmModalVisible(true)} className="ms-2"><CIcon icon={cilCommentBubble} className="me-1" /> {t('camera.sync_llm')}</CButton>}
                                    <CButton color="warning" variant="outline" size="sm" onClick={() => setRecalculateModalVisible(true)}><CIcon icon={cilSync} className="me-1" /> {t('camera.recalc_all')}</CButton>
                                </CButtonGroup>
                            </CCardHeader>
                            <CCardBody>
                                <CRow className="align-items-end mb-3">
                                    <CCol md={5}><label>{t('common.date_start')}</label><DatePicker selected={startDate} onChange={(date) => setStartDate(date)} selectsStart startDate={startDate} endDate={endDate} showTimeSelect className="form-control" dateFormat="dd.MM.yyyy HH:mm" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></CCol>
                                    <CCol md={5}><label>{t('common.date_end')}</label><DatePicker selected={endDate} onChange={(date) => setEndDate(date)} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} showTimeSelect className="form-control" dateFormat="dd.MM.yyyy HH:mm" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></CCol>
                                    <CCol md={2}><CButton color="primary" onClick={() => fetchReports()} disabled={loadingAnalytics} className="w-100">{loadingAnalytics ? <CSpinner size="sm"/> : t('common.get_report')}</CButton></CCol>
                                </CRow>
                                {loadingAnalytics && <div className="text-center p-3"><CSpinner/></div>}
                                
                                {analytics && !loadingAnalytics && (
                                    <>
                                        <h5 className="mb-3">{t('camera.summary_period')} (UTC{camera.timezone_offset >= 0 ? '+' : ''}{3 + camera.timezone_offset})</h5>
                                        
                                        {/* Toggles for Timelines */}
                                        <div className="mb-3 p-2 bg-light border rounded d-flex gap-3 align-items-center flex-wrap">
                                            <span className="text-muted small text-uppercase fw-bold me-2"><CIcon icon={cilOptions} /> {t('camera.view_toggle_label')}</span>
                                            
                                            <CFormCheck 
                                                type="radio" 
                                                name="viewToggle" 
                                                id="view-general" 
                                                label={t('camera.view_general')} 
                                                checked={activeView === 'general'} 
                                                onChange={() => setActiveView('general')}
                                            />
                                            <CFormCheck 
                                                type="radio" 
                                                name="viewToggle" 
                                                id="view-chair" 
                                                label={t('camera.view_chair')} 
                                                checked={activeView === 'chair'} 
                                                onChange={() => setActiveView('chair')}
                                            />
                                            <CFormCheck 
                                                type="radio" 
                                                name="viewToggle" 
                                                id="view-phone" 
                                                label={t('camera.view_phone')} 
                                                checked={activeView === 'phone'} 
                                                onChange={() => setActiveView('phone')}
                                            />
                                        </div>

                                        {/* Auto Service Logic */}
                                        {analytics.mode === 'auto_service' && (
                                            <>
                                                {/* Summary Stats Card */}
                                                <CCard className="mb-4">
                                                    <CCardHeader>Сводка по технике</CCardHeader>
                                                    <CCardBody>
                                                        <CListGroup flush>
                                                            <CListGroupItem><strong>{t('camera.total_entries')}:</strong> {analytics.vehicle_entries_count}</CListGroupItem>
                                                            <CListGroupItem><strong>{t('camera.unique_vehicles')}:</strong> {groupedVehicleLogs.length}</CListGroupItem>
                                                            <CListGroupItem><strong>{t('camera.total_downtime')}:</strong> {formatDuration(analytics.general_stats.total_downtime_seconds)}</CListGroupItem>
                                                            <CListGroupItem><strong>{t('camera.total_idle')}:</strong> {formatDuration(analytics.general_stats.total_idle_time_seconds)}</CListGroupItem>
                                                        </CListGroup>
                                                    </CCardBody>
                                                </CCard>

                                                {/* ИСПОЛЬЗУЕМ МЕМОИЗИРОВАННЫЕ ДАННЫЕ */}
                                                {activeView === 'general' && generalData && (
                                                    <AnalyticsBlock 
                                                        title={t('camera.activity_general_title')}
                                                        analyticsData={generalData}
                                                        classIdToMeta={autoServiceClassIdToMeta}
                                                        onSegmentDoubleClick={handleSegmentDoubleClick}
                                                        localTimezoneOffset={camera?.timezone_offset}
                                                        type="general"
                                                        operatingMode={analytics.mode}
                                                    />
                                                )}
                                                {activeView === 'chair' && chairData && (
                                                    <AnalyticsBlock 
                                                        title={t('camera.activity_chair_title')}
                                                        analyticsData={chairData}
                                                        timelineDataOverride={chairTimelineToShow}
                                                        classIdToMeta={chairMeta}
                                                        onSegmentDoubleClick={handleSegmentDoubleClick}
                                                        localTimezoneOffset={camera?.timezone_offset}
                                                        type="chair"
                                                        operatingMode={analytics.mode}
                                                    />
                                                )}
                                                {activeView === 'phone' && phoneData && (
                                                    <AnalyticsBlock 
                                                        title={t('camera.activity_phone_title')}
                                                        analyticsData={phoneData}
                                                        timelineDataOverride={phoneTimelineToShow}
                                                        classIdToMeta={phoneMeta}
                                                        onSegmentDoubleClick={handleSegmentDoubleClick}
                                                        localTimezoneOffset={camera?.timezone_offset}
                                                        type="phone"
                                                        operatingMode={analytics.mode}
                                                    />
                                                )}
                                            </>
                                        )}

                                        {/* Personnel Control Logic */}
                                        {analytics.mode === 'personnel_control' && (
                                            <>
                                                {activeView === 'general' && generalData && (
                                                    <AnalyticsBlock 
                                                        title={t('camera.activity_general_title')}
                                                        analyticsData={generalData}
                                                        classIdToMeta={personnelGeneralMeta}
                                                        onSegmentDoubleClick={handleSegmentDoubleClick}
                                                        localTimezoneOffset={camera?.timezone_offset}
                                                        type="general"
                                                        operatingMode={analytics.mode}
                                                    />
                                                )}
                                                {activeView === 'chair' && chairData && (
                                                    <AnalyticsBlock 
                                                        title={t('camera.activity_chair_title')}
                                                        analyticsData={chairData}
                                                        classIdToMeta={chairMeta}
                                                        onSegmentDoubleClick={handleSegmentDoubleClick}
                                                        localTimezoneOffset={camera?.timezone_offset}
                                                        type="chair"
                                                        operatingMode={analytics.mode}
                                                    />
                                                )}
                                                {activeView === 'phone' && phoneData && (
                                                    <AnalyticsBlock 
                                                        title={t('camera.activity_phone_title')}
                                                        analyticsData={phoneData}
                                                        classIdToMeta={phoneMeta}
                                                        onSegmentDoubleClick={handleSegmentDoubleClick}
                                                        localTimezoneOffset={camera?.timezone_offset}
                                                        type="phone"
                                                        operatingMode={analytics.mode}
                                                    />
                                                )}
                                            </>
                                        )}

                                        {/* Common Video Player Section */}
                                        {(selectedVideos.primary || selectedVideos.secondary) && (
                                            <CRow className="mt-4">
                                                {selectedVideos.primary && (
                                                    <CCol md={12}>
                                                        <CCard>
                                                            <CCardHeader>{sourceCameraNames.primary}</CCardHeader>
                                                            <CCardBody className='p-0'>
                                                                <video ref={videoRefPrimary} key={selectedVideos.primary.url} controls width="100%">
                                                                    <source src={selectedVideos.primary.url} type="video/mp4" />
                                                                </video>
                                                            </CCardBody>
                                                        </CCard>
                                                    </CCol>
                                                )}
                                                {camera.is_combined && selectedVideos.secondary && (
                                                    <CCol md={12} style={{marginTop:"10px"}}>
                                                        <CCard>
                                                            <CCardHeader>{sourceCameraNames.secondary}</CCardHeader>
                                                            <CCardBody className='p-0'>
                                                                <video ref={videoRefSecondary} key={selectedVideos.secondary.url} controls width="100%">
                                                                    <source src={selectedVideos.secondary.url} type="video/mp4" />
                                                                </video>
                                                            </CCardBody>
                                                        </CCard>
                                                    </CCol>
                                                )}
                                            </CRow>
                                        )}

                                        {/* Vehicle Logs (Auto Service only) */}
                                        {camera?.operating_mode === 'auto_service' && (
                                            <>
                                                <h5 className="mt-4">{t('camera.analytics_llm')}</h5>
                                                {groupedVehicleLogs.length > 0 ? (
                                                    <CAccordion flush>
                                                        {groupedVehicleLogs.map((group, groupIndex) => (
                                                            <CAccordionItem itemKey={group.id} key={group.id}>
                                                                <CAccordionHeader className="clickable-header">
                                                                    <div className='d-flex align-items-center w-100 justify-content-between pe-3'>
                                                                        <div className='d-flex align-items-center'><CIcon icon={cilCarAlt} className="me-2" size="lg" /><strong>{t('camera.vehicle_group_label', {index: groupIndex + 1})}</strong><CBadge color="info" shape="rounded-pill" className="ms-2">{t('camera.visits_count', {count: group.logs.length})}</CBadge>{group.total_man_hours > 0 && <CBadge color="warning" className="ms-3">{t('camera.man_hours', {hours: group.total_man_hours.toFixed(1)})}</CBadge>}</div>
                                                                        <div className='d-flex align-items-center'><CButton size="sm" color="light" className="me-3" title={t('camera.show_group_report')} onClick={(e) => { e.stopPropagation(); setStartDate(dayjs(group.startTime).toDate()); setEndDate(dayjs(group.endTime).toDate()); fetchReports(dayjs(group.startTime).toDate(), dayjs(group.endTime).toDate()); }}><CIcon icon={cilChart} /></CButton><small className="text-muted">{formatLocalTime(group.startTime)} - {formatLocalTime(group.endTime)}</small></div>
                                                                    </div>
                                                                </CAccordionHeader>
                                                                <CAccordionBody>
                                                                    <CAccordion flush>
                                                                        {group.logs.map((log) => {
                                                                            const comparisonComment = analytics.vehicle_analysis_comments.find(c => c.first_vehicle_log_id === log.id);
                                                                            return (
                                                                            <React.Fragment key={log.id}>
                                                                                <CAccordionItem itemKey={log.id}>
                                                                                    <CAccordionHeader className="clickable-header">
                                                                                        <div className="d-flex w-100 justify-content-between align-items-center pe-3">
                                                                                            <div><CIcon icon={cilTask} className="me-2 text-primary" /><strong>{t('camera.visit_label', {id: log.id})}: {formatLocalTime(log.entry_time)} - {formatLocalTime(log.exit_time)}</strong></div>
                                                                                            <div className='d-flex align-items-center'><CButton size="sm" color="light" className="me-2" title={t('camera.move_modal_title')} onClick={(e) => { e.stopPropagation(); setMoveModal({ visible: true, log: log, targetGroupId: '' });}}><CIcon icon={cilMove} /></CButton><CButton size="sm" color="light" className="me-2" title={t('common.get_report')} onClick={(e) => { e.stopPropagation(); setStartDate(dayjs(log.entry_time).toDate()); setEndDate(dayjs(log.exit_time).toDate()); fetchReports(dayjs(log.entry_time).toDate(), dayjs(log.exit_time).toDate());}}><CIcon icon={cilChart} /></CButton><span className='text-muted'>{t('camera.duration_label')} {formatDuration(dayjs(log.exit_time).diff(dayjs(log.entry_time), 'second'))}</span></div>
                                                                                        </div>
                                                                                    </CAccordionHeader>
                                                                                    <CAccordionBody>
                                                                                        {log.work_analysis_text ? (
                                                                                            <>
                                                                                                <h6>{t('camera.analysis_works')}</h6><pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', background: '#f8f9fa', padding: '10px', borderRadius: '5px' }}>{log.work_analysis_text}</pre>
                                                                                                <CRow><CCol md={6}><p className="text-center small text-muted">{t('camera.start_frame')}</p>{log.first_frame_for_analysis_url ? <img src={log.first_frame_for_analysis_url} alt="First frame" style={{ width: '100%', borderRadius: '5px' }} /> : <p>{t('camera.no_photo')}</p>}</CCol><CCol md={6}><p className="text-center small text-muted">{t('camera.end_frame')}</p>{log.last_frame_for_analysis_url ? <img src={log.last_frame_for_analysis_url} alt="Second frame" style={{ width: '100%', borderRadius: '5px' }} /> : <p>{t('camera.no_photo')}</p>}</CCol></CRow>
                                                                                            </>
                                                                                        ) : (<div className="text-center text-muted p-3"><CIcon icon={cilWarning} className="me-2"/>{t('camera.no_work_analysis')}</div>)}
                                                                                        {comparisonComment && (<div className="mt-3 pt-3 border-top"><h6>{t('camera.comparison_next')}</h6><p className="mb-1"><strong>LLM:</strong> {comparisonComment.comment_text}</p><p className="mb-0"><strong>ORB:</strong><CBadge color={comparisonComment.is_same_vehicle ? "success" : "danger"} className="ms-2 me-1">{comparisonComment.similarity_percentage} {t('camera.matches')}</CBadge>({t('camera.threshold')}: {comparisonComment.comparison_details?.threshold || 'N/A'})</p></div>)}
                                                                                    </CAccordionBody>
                                                                                </CAccordionItem>
                                                                            </React.Fragment>
                                                                        )})}
                                                                    </CAccordion>
                                                                </CAccordionBody>
                                                            </CAccordionItem>
                                                        ))}
                                                    </CAccordion>
                                                ) : <p className="text-muted">No vehicle visits.</p>}
                                            </>
                                        )}

                                        <h5 className="mt-4">{t('camera.video_archive')}</h5>
                                        {videos.length > 0 ? (
                                            <CAccordion alwaysOpen>
                                                {videos.map((dailyArchive, index) => (
                                                    <CAccordionItem itemKey={index} key={dailyArchive.date}>
                                                        <CAccordionHeader className="clickable-header">{dayjs(dailyArchive.date).format('DD MMMM YYYY')}</CAccordionHeader>
                                                        <CAccordionBody>
                                                            <CListGroup>
                                                                {Object.entries(dailyArchive.data).map(([sourceCamId, vidList]) => (
                                                                    <div key={sourceCamId}>
                                                                        {camera.is_combined && <h6 className="mt-2 mb-2 text-primary">{sourceCamId == (camera.parent || camera.id) ? sourceCameraNames.primary : sourceCameraNames.secondary}</h6>}
                                                                        {vidList.length > 0 ? vidList.map(v => (
                                                                            <CListGroupItem key={v.id} className="d-flex justify-content-between align-items-center">
                                                                                <span>{dayjs(v.start_time).format('HH:00')} - {dayjs(v.end_time).format('HH:00')} (MSK)</span>
                                                                                <div>
                                                                                    <CButton color="info" size="sm" className="me-2 text-white" onClick={() => { const isPrimary = sourceCamId == (camera.parent || camera.id); setSelectedVideos(prev => ({ ...prev, [isPrimary ? 'primary' : 'secondary']: { url: v.video_file, seekTime: 0, seekId: Date.now() } })); toast.info(t('camera.vehicle_video_loaded', {name: isPrimary ? sourceCameraNames.primary : sourceCameraNames.secondary})); }}><CIcon icon={cilCast} /> {t('camera.watch')}</CButton>
                                                                                    <a href={v.video_file} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success text-white"><CIcon icon={cilCloudDownload} /></a>
                                                                                </div>
                                                                            </CListGroupItem>
                                                                        )) : <div className="text-muted small ps-3">No videos</div>}
                                                                    </div>
                                                                ))}
                                                            </CListGroup>
                                                        </CAccordionBody>
                                                    </CAccordionItem>
                                                ))}
                                            </CAccordion>
                                        ) : <p className="text-muted">{t('camera.no_videos')}</p>}
                                    </>
                                )}
                                {!analytics && !loadingAnalytics && <p className="text-center text-muted">{t('camera.select_period_msg')}</p>}
                            </CCardBody>
                        </CCard>
                    </CCol>
                </CRow>
            </CContainer>

            {/* DEBUG DRAGGABLE WINDOW */}
            {roiDebugMode && (
                <Draggable handle=".roi-debug-handle" bounds="parent">
                    <div style={{position: 'absolute', top: '150px', left: '50px', width: '350px', maxHeight: '400px', zIndex: 1050, background: 'white', border: '1px solid #dee2e6', borderRadius: '0.375rem', boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)', display: 'flex', flexDirection: 'column'}}>
                        <div className="roi-debug-handle" style={{ padding: '0.75rem 1rem', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', cursor: 'move', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <h6 className="mb-0">{t('camera.roi_debug_title')}</h6>
                            <CButton color="secondary" variant="ghost" size="sm" onClick={() => setRoiDebugMode(false)} title="Close" style={{ padding: '0.1rem 0.4rem' }}><CIcon icon={cilX} /></CButton>
                        </div>
                        <div style={{padding: '1rem', overflowY: 'auto', color: '#212529', fontFamily: 'monospace', fontSize: '13px'}}>
                            {!roiDebugInfo ? (<div className='text-center'><CSpinner size="sm" /> {t('common.loading')}</div>) : (
                                <>
                                    <p className='mb-2 small text-muted'>{t('camera.roi_debug_frame_time')} {dayjs(roiDebugInfo.frame_timestamp).format('HH:mm:ss')}</p>
                                    {roiDebugInfo.boxes_data.length === 0 ? (<p className="m-0 text-muted">{t('camera.roi_debug_no_objects')}</p>) : (
                                        roiDebugInfo.boxes_data.map((box) => (
                                            <div key={box.track_id} style={{ borderLeft: `3px solid ${box.is_in_roi ? '#2eb85c' : '#e55353'}`, paddingLeft: '8px', marginBottom: '8px'}}>
                                                <div><strong>{t('camera.roi_debug_track_id')} {box.track_id}</strong> ({box.class_name})</div>
                                                <div>{t('camera.roi_debug_intersection')} {(box.area_percentage * 100).toFixed(2)}%</div>
                                                <div>{t('camera.roi_debug_occupancy')} {(box.occupancy_percentage * 100).toFixed(3)}%</div>
                                            </div>
                                        ))
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </Draggable>
            )}
            
            {/* MODALS */}
            <CModal alignment="center" visible={recalculateModalVisible} onClose={() => setRecalculateModalVisible(false)}>
                <CModalHeader><CModalTitle>{t('camera.recalc_modal_title')}</CModalTitle></CModalHeader>
                <CModalBody>
                    <p>{t('camera.recalc_modal_body')}</p>
                    <div className="d-flex flex-column"><label>{t('common.date_start')}:</label><DatePicker selected={recalculationDate} onChange={(date) => setRecalculationDate(date)} showTimeSelect className="form-control" dateFormat="dd.MM.yyyy HH:mm" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></div>
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" onClick={() => setRecalculateModalVisible(false)} disabled={isRecalculating}>{t('common.cancel')}</CButton>
                    <CButton color="primary" onClick={handleRecalculate} disabled={isRecalculating}>{isRecalculating ? <CSpinner size="sm" /> : t('camera.start_recalc')}</CButton>
                </CModalFooter>
            </CModal>
            
            <CModal alignment="center" visible={llmModalVisible} onClose={() => setLlmModalVisible(false)}>
                <CModalHeader><CModalTitle>{t('camera.llm_modal_title')}</CModalTitle></CModalHeader>
                <CModalBody>
                    <p>{t('camera.llm_modal_body')}</p>
                    <div className="d-flex flex-column"><label>{t('common.date_start')}:</label><DatePicker selected={llmSyncDate} onChange={(date) => setLlmSyncDate(date)} showTimeSelect className="form-control" dateFormat="dd.MM.yyyy HH:mm" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></div>
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" onClick={() => setLlmModalVisible(false)} disabled={isLlmSyncing}>{t('common.cancel')}</CButton>
                    <CButton color="primary" onClick={handleLlmSync} disabled={isLlmSyncing}>{isLlmSyncing ? <CSpinner size="sm" /> : t('camera.start_sync')}</CButton>
                </CModalFooter>
            </CModal>

            <CModal alignment="center" visible={moveModal.visible} onClose={() => setMoveModal({ visible: false, log: null, targetGroupId: '' })}>
                <CModalHeader><CModalTitle>{t('camera.move_modal_title')} #{moveModal.log?.id}</CModalTitle></CModalHeader>
                <CModalBody>
                    <CForm>
                        <CFormLabel>{t('camera.move_target_label')}</CFormLabel>
                        <CFormSelect value={moveModal.targetGroupId} onChange={(e) => setMoveModal(prev => ({ ...prev, targetGroupId: e.target.value }))}>
                            <option value="">{t('camera.move_option_default')}</option>
                            {groupedVehicleLogs.filter(g => !g.logs.some(l => l.id === moveModal.log?.id)).map((group, index) => (<option key={group.id} value={group.logs[0].id}>{t('camera.vehicle_group_label', {index: index + 1})} ({t('camera.visit_label', {id: group.logs[0].id})})</option>))}
                            <option value="new_group">{t('camera.move_option_new')}</option>
                        </CFormSelect>
                    </CForm>
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" onClick={() => setMoveModal({ visible: false, log: null, targetGroupId: '' })}>{t('common.cancel')}</CButton>
                    <CButton color="primary" onClick={handleMoveLog}>{t('camera.move_btn')}</CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default CameraDetailPage;